/* eslint-disable object-shorthand */
import { useCallback } from 'react';
import { isAfter, addMinutes } from 'date-fns';
import memoize from 'memoize-one';
import { graphql } from 'react-relay';

import type { ClientBasedAuth } from '@atlaskit/media-core/auth';

import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	ContentMediaSessionQuery_contentMediaSession,
	ContentMediaSessionQuery as ContentMediaSessionQueryType,
	ContentMediaSessionQueryVariables,
	ContentMediaSessionQuery_contentMediaSession as ContentMediaSessionType,
} from './__types__/ContentMediaSessionQuery.ts';
import type {
	ContentReadOnlyMediaSessionQuery as ContentReadOnlyMediaSessionQueryType,
	ContentReadOnlyMediaSessionQueryVariables,
} from './__types__/ContentReadOnlyMediaSessionQuery.ts';
import { ContentMediaSessionQuery } from './ContentMediaSession.graphql.ts';
import { ContentReadOnlyMediaSessionQuery } from './ContentReadOnlyMediaSession.graphql.ts';

type ContentMediaSessionQueryStrict = {
	contentMediaSession: ContentMediaSessionQuery_contentMediaSession;
};

type ContentReadOnlyMediaSessionQueryStrict = {
	contentMediaSession: NonNullable<ContentReadOnlyMediaSessionQueryType['contentMediaSession']> & {
		accessTokens: { readOnlyToken: { value: string } };
	};
};

const isMediaSessionQueryDataValid = (
	data?: ContentMediaSessionQueryType | null,
): data is ContentMediaSessionQueryStrict => Boolean(data?.contentMediaSession);

const isReadOnlyMediaSessionQueryDataValid = (
	data?: ContentReadOnlyMediaSessionQueryType | null,
): data is ContentReadOnlyMediaSessionQueryStrict =>
	Boolean(
		data?.contentMediaSession?.accessTokens?.readOnlyToken?.value &&
		data?.contentMediaSession?.configuration &&
		!!data.contentMediaSession.configuration.clientId &&
		!!data.contentMediaSession.configuration.fileStoreUrl,
	);

const createMediaSessionConfig = memoize(
	(data: ContentMediaSessionQueryStrict): ClientBasedAuth => ({
		clientId: data.contentMediaSession.configuration.clientId,
		baseUrl: data.contentMediaSession.configuration.fileStoreUrl,
		token: data.contentMediaSession.token.value,
	}),
);

type UseAuthProviderProps = {
	contentId: string | undefined;
	// take into account that authProvider function will be deffered to the media client
	// and will be invoked when user starts uploading a file
	// onMediaSessionError might be triggered on that moment if there is an error with getting the token
	onMediaSessionError?: (err: Error) => void;
	/**
	 * When true, fetches a read-only media token instead of a read-write upload token.
	 * Use this for view-only contexts (e.g. rendering existing attachments) to avoid
	 * failing for users who lack upload attachment permissions.
	 */
	readOnly?: boolean;
};

export const useAuthProvider = ({
	contentId,
	onMediaSessionError,
	readOnly,
}: UseAuthProviderProps): (() => Promise<ClientBasedAuth>) | null => {
	const readMediaSessionFragment = useReadFragmentInterop<ContentMediaSessionType>({
		fragmentSpec: graphql`
			fragment useAuthProvider_mediaSession on ContentMediaSession {
				token {
					value
					duration
					expiryDateTime
				}
				configuration {
					clientId
					fileStoreUrl
				}
			}
		`,
	});

	const getMediaSessionFromCache = useCallback(() => {
		try {
			if (!contentId) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' contentId is not provided.",
				);
			}

			let cachedData;
			cachedData = readMediaSessionFragment({
				id: `$ROOT_QUERY.contentMediaSession(${JSON.stringify({ contentId: contentId })})`,
			});
			// wrapper to fit expectations around original query code data shape
			cachedData = { contentMediaSession: cachedData };
			if (!isMediaSessionQueryDataValid(cachedData)) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' invalid or missing data received from cache.",
				);
			}
			return cachedData;
		} catch {
			// expected behavior
			// "readQuery" throws if there is no data in the store
			return;
		}
	}, [contentId, readMediaSessionFragment]);

	const getMediaSessionFromServer = useCallback(async () => {
		try {
			if (!contentId) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' contentId is not provided.",
				);
			}
			const response = await queryInterop<
				ContentMediaSessionQueryType,
				ContentMediaSessionQueryVariables
			>({
				query: ContentMediaSessionQuery,
				variables: { contentId },
				fetchPolicy: 'network-only',
			});
			if (!isMediaSessionQueryDataValid(response.data)) {
				throw new Error(
					"[useAuthProvider] For 'ContentMediaSessionQuery' invalid or missing data received from server.",
				);
			}
			if (response?.error) {
				throw response.error;
			}
			return response.data;
		} catch (err) {
			if (onMediaSessionError) {
				onMediaSessionError(err);
			}
			throw err;
		}
	}, [contentId, onMediaSessionError]);

	const getReadOnlyMediaSessionFromServer = useCallback(async (): Promise<ClientBasedAuth> => {
		try {
			if (!contentId) {
				throw new Error(
					"[useAuthProvider] For 'ContentReadOnlyMediaSessionQuery' contentId is not provided.",
				);
			}
			const response = await queryInterop<
				ContentReadOnlyMediaSessionQueryType,
				ContentReadOnlyMediaSessionQueryVariables
			>({
				query: ContentReadOnlyMediaSessionQuery,
				variables: { contentId },
				fetchPolicy: 'network-only',
			});
			if (response?.error) {
				throw response.error;
			}
			if (!isReadOnlyMediaSessionQueryDataValid(response.data)) {
				throw new Error(
					"[useAuthProvider] For 'ContentReadOnlyMediaSessionQuery' invalid or missing data received from server.",
				);
			}
			const session = response.data.contentMediaSession;
			return {
				clientId: session.configuration.clientId,
				baseUrl: session.configuration.fileStoreUrl,
				token: session.accessTokens.readOnlyToken.value,
			};
		} catch (err) {
			if (onMediaSessionError) {
				onMediaSessionError(err);
			}
			throw err;
		}
	}, [contentId, onMediaSessionError]);

	// Builds the media session config, including the token, from cache or server
	// It doesn't retry.
	const authProvider = useCallback(async () => {
		if (readOnly) {
			return getReadOnlyMediaSessionFromServer();
		}

		// countentId should be defined, otherwise this function shouldn't be called
		const cachedMediaSession = getMediaSessionFromCache();

		if (cachedMediaSession) {
			const expirationDateTime = new Date(
				cachedMediaSession.contentMediaSession.token.expiryDateTime,
			);
			// It's expected to expire in 10mins. We'll renew it after 8mins.
			if (!isAfter(new Date(), addMinutes(expirationDateTime, -2))) {
				return createMediaSessionConfig(cachedMediaSession);
			}
		}

		const freshMediaSession = await getMediaSessionFromServer();
		return createMediaSessionConfig(freshMediaSession);
	}, [
		readOnly,
		getReadOnlyMediaSessionFromServer,
		getMediaSessionFromCache,
		getMediaSessionFromServer,
	]);

	if (!contentId) {
		return null;
	}

	return authProvider;
};
